/**
 * Dashboard Context
 * Manages global filter state and data fetching for the dashboard.
 * URL is single source of truth for filters (store, stylist, month) — survives refresh, shareable, back/forward.
 * Persists last filters to localStorage (keyed by uid) for restore when user returns to / with empty URL.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { fetchStores, fetchStylists, fetchMonths, fetchKPIs, fetchStoreAverageKPIs, fetchKPIHistory, fetchMLForecasts, fetchAIComment } from '../api/dashboardApi';
import { fetchMyIdentity } from '../api/authApi';
import { getLatestMonth, matchStylistForUser } from '../utils/dashboardFilters';
import { jstMonth, jstToday } from '../utils/jstDate';
import { useUserRole } from '../hooks/useUserRole';
import {
  filterKey as makeFilterKey,
  readCachedKpis,
  writeCachedKpis,
  readCachedKpiHistory,
  writeCachedKpiHistory,
  readDaySnapshot,
  writeDaySnapshot,
} from '../utils/kpiSnapshot';

/** POS placeholder names that are not people (unassigned / walk-in bookings). */
const PLACEHOLDER_STYLISTS = new Set(['フリー', 'ﾌﾘｰ', 'free', 'FREE']);

const FILTER_STORAGE_KEY = (uid) => `dashboard_filters_${uid}`;

const DashboardContext = createContext(null);

export function DashboardProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { role, loading: roleLoading } = useUserRole();

  // Filter state from URL (single source of truth)
  const selectedStore = searchParams.get('store') || null;
  const selectedStylist = searchParams.get('stylist') || null;
  const selectedMonth = searchParams.get('month') || null;

  // Own identity from /auth/me (home_store + stylist_name_pos). Shared with FirstVisitOverlay.
  // undefined = not known yet; null = nothing to use (dev, no role, or the fetch failed).
  const [myProfile, setMyProfile] = useState(undefined);
  const uid = user?.uid || null;
  useEffect(() => {
    if (!uid) {
      setMyProfile(undefined);
      return undefined;
    }
    if (roleLoading) return undefined;
    if (!role || role === 'dev') {
      setMyProfile(null);
      return undefined;
    }
    let cancelled = false;
    fetchMyIdentity()
      .then((p) => {
        if (!cancelled) setMyProfile(p && typeof p === 'object' ? p : null);
      })
      .catch(() => {
        if (!cancelled) setMyProfile(null); // unknown → fall back to the saved filters below
      });
    return () => {
      cancelled = true;
    };
  }, [uid, role, roleLoading]);

  const homeStore = myProfile?.home_store && myProfile?.stylist_name_pos ? myProfile.home_store : null;
  const ownStylist = homeStore ? myProfile.stylist_name_pos : null;

  // Once per page load: fill filters. Waits for /auth/me so the app never loads one
  // store and then jumps to another. A bound user always opens on their main store
  // (home_store) + own record — over the URL and over the store this browser remembered
  // (owner decision 2026-09-29). Afterwards the picker changes store freely.
  // Otherwise fill store/stylist from localStorage. Never overwrite URL month
  // (testers pick months on purpose). Month is restored only when URL has no filters at all.
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!uid) hasRestoredRef.current = false; // signed out → next sign-in is a new open
  }, [uid]);
  useEffect(() => {
    if (!user || hasRestoredRef.current || myProfile === undefined) return;
    hasRestoredRef.current = true;
    let saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY(user.uid)) || '{}') || {};
    } catch {
      saved = {};
    }
    const urlEmpty = !selectedStore && !selectedStylist && !selectedMonth;
    const useHome = Boolean(homeStore) && (selectedStore !== homeStore || selectedStylist !== ownStylist);
    const store = useHome ? homeStore : !selectedStore ? saved.store : null;
    const stylist = useHome ? ownStylist : !selectedStylist ? saved.stylist : null;
    // Never restore a month that has already passed — a stylist returning on the
    // 1st would land on last month's screen. Leaving it empty lets the
    // "default month" effect below pick the latest month the API actually has.
    const month = urlEmpty && saved.month && saved.month >= jstMonth() ? saved.month : null;
    if (!store && !stylist && !month) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (store) next.set('store', store);
      if (stylist) next.set('stylist', stylist);
      if (month) next.set('month', month);
      return next;
    }, { replace: true });
  }, [user, myProfile, homeStore, ownStylist, selectedStore, selectedStylist, selectedMonth, setSearchParams]);

  // Persist filters — never overwrite a known store/stylist with null when the URL
  // only has month (settings deep-link). That wipe blocked restore + KPI fetch.
  useEffect(() => {
    if (!user || (!selectedStore && !selectedStylist && !selectedMonth)) return;
    try {
      let prev = {};
      try {
        prev = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY(user.uid)) || '{}') || {};
      } catch {
        prev = {};
      }
      localStorage.setItem(
        FILTER_STORAGE_KEY(user.uid),
        JSON.stringify({
          store: selectedStore || prev.store || null,
          stylist: selectedStylist || prev.stylist || null,
          month: selectedMonth || prev.month || null,
        })
      );
    } catch {}
  }, [user, selectedStore, selectedStylist, selectedMonth]);

  const updateStore = useCallback((store, opts = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (store) next.set('store', store);
      else next.delete('store');
      next.delete('stylist'); // stylist list changes when store changes
      return next;
    }, opts);
  }, [setSearchParams]);

  const updateStylist = useCallback((stylist, opts = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (stylist) next.set('stylist', stylist);
      else next.delete('stylist');
      return next;
    }, opts);
  }, [setSearchParams]);

  const updateMonth = useCallback((month, opts = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (month) next.set('month', month);
      else next.delete('month');
      return next;
    }, opts);
  }, [setSearchParams]);

  // The JST day everything on screen is "as of". The home card is generated per day,
  // so a tab left open across midnight has to notice and refetch — otherwise the
  // stylist reads yesterday's card all morning.
  const [today, setToday] = useState(() => jstToday());

  useEffect(() => {
    const check = () => {
      const now = jstToday();
      setToday((prev) => (prev === now ? prev : now));
    };
    // Re-check when the tab comes back, and hourly for a screen left visible.
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    const timer = setInterval(check, 60 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      clearInterval(timer);
    };
  }, []);

  // Data state — hydrate KPI snapshot from sessionStorage so Settings/refresh paints immediately
  // Lists are keyed by uid: what a person may see depends on their role/company.
  const [stores, setStores] = useState(() => readDaySnapshot('stores', uid) || []);
  const [stylists, setStylists] = useState(() => readDaySnapshot('stylists', selectedStore)?.stylists || []);
  // POS names in this store with no active account (未登録) — per-store numbers, labelled in the picker
  const [unregisteredStylists, setUnregisteredStylists] = useState(
    () => readDaySnapshot('stylists', selectedStore)?.unregistered || []
  );
  const [months, setMonths] = useState(() => readDaySnapshot('months', uid) || []);
  const [kpis, setKpis] = useState(
    () => readCachedKpis(selectedStore, selectedStylist, selectedMonth)?.kpis ?? null
  );
  const [storeAverageKpis, setStoreAverageKpis] = useState(
    () => readDaySnapshot('storeAvg', `${selectedStore || ''}|${selectedMonth || ''}`) ?? null
  );
  // Latest visit_date behind the numbers — the ETL lands each morning covering
  // through the previous day, so this is not the same as "today".
  const [dataAsOf, setDataAsOf] = useState(null);
  // Registered person, month split over more than one store: [{store, total_sales, customer_count, main}]
  const [kpiBreakdown, setKpiBreakdown] = useState(null);
  const [kpiHistory, setKpiHistory] = useState(
    () => readCachedKpiHistory(selectedStore, selectedStylist)?.history ?? []
  );
  const [mlForecasts, setMlForecasts] = useState(
    () => readDaySnapshot('forecasts', `${selectedStore || ''}|${selectedStylist || ''}`) || []
  );
  // AI comment snapshot is keyed by store|stylist|month only; the day is implied by the
  // snapshot's own date rule, which is what the home card is generated per.
  const [aiSnap] = useState(() =>
    readDaySnapshot('aiComment', `${selectedStore || ''}|${selectedStylist || ''}|${selectedMonth || ''}`)
  );
  const [aiComment, setAiComment] = useState(aiSnap?.s4 ?? null);
  const [aiCommentSection1, setAiCommentSection1] = useState(aiSnap?.s1 ?? null);
  const [aiCommentSection2, setAiCommentSection2] = useState(aiSnap?.s2 ?? null);
  const [aiCommentSection3, setAiCommentSection3] = useState(aiSnap?.s3 ?? null);

  // Loading state
  const [loading, setLoading] = useState({
    stores: false,
    stylists: false,
    months: false,
    kpis: false,
    storeAverageKpis: false,
    kpiHistory: false,
    mlForecasts: false,
    aiComment: false,
    aiCommentSection1: false,
    aiCommentSection2: false,
    aiCommentSection3: false
  });

  // Error state
  const [error, setError] = useState(null);

  // Marks which store|stylist|month KPI snapshot is already in memory (skip re-BQ)
  const loadedKpiKeyRef = useRef('');

  // Load stores on mount. URL is source of truth — only user actions update URL, NOT API responses.
  useEffect(() => {
    async function loadStores() {
      const cached = readDaySnapshot('stores', uid);
      if (cached) setStores(cached);
      else setLoading(prev => ({ ...prev, stores: true }));
      try {
        const data = await fetchStores();
        setStores(data.stores);
        writeDaySnapshot('stores', uid, data.stores);
      } catch (err) {
        setError(`Failed to load stores: ${err.message}`);
        if (process.env.NODE_ENV === 'development') console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, stores: false }));
      }
    }
    loadStores();
    // uid: the snapshot key. fetchStores() itself is deduped for 5 min in dashboardApi.
  }, [uid]);

  // Validate: if saved store was deleted by admin, clear invalid filters
  useEffect(() => {
    if (!selectedStore || stores.length === 0) return;
    if (!stores.includes(selectedStore)) {
      updateStore(null);
      if (user) {
        try {
          localStorage.removeItem(FILTER_STORAGE_KEY(user.uid));
        } catch {}
      }
    }
  }, [stores, selectedStore, user, updateStore]);

  // Default month → latest available when URL has no month
  useEffect(() => {
    if (months.length === 0 || selectedMonth) return;
    const latest = getLatestMonth(months);
    if (latest) updateMonth(latest, { replace: true });
  }, [months, selectedMonth, updateMonth]);

  // Replace month if URL/localStorage value is not in API list
  useEffect(() => {
    if (months.length === 0 || !selectedMonth) return;
    if (!months.includes(selectedMonth)) {
      const latest = getLatestMonth(months);
      if (latest) updateMonth(latest, { replace: true });
    }
  }, [months, selectedMonth, updateMonth]);

  // Auto-select store when user has access to exactly one
  useEffect(() => {
    if (!user || selectedStore || stores.length !== 1) return;
    updateStore(stores[0], { replace: true });
  }, [user, stores, selectedStore, updateStore]);

  // Auto-select own stylist when the roster has it (non-dev only): the bound POS name
  // exactly, else Firebase displayName (partial match can pick a longer name).
  useEffect(() => {
    if (role === 'dev') return;
    if (!user || selectedStylist || !selectedStore || stylists.length === 0) return;
    const match =
      ownStylist && stylists.includes(ownStylist)
        ? ownStylist
        : matchStylistForUser(user.displayName, stylists);
    if (match) updateStylist(match, { replace: true });
  }, [user, selectedStylist, selectedStore, stylists, updateStylist, role, ownStylist]);

  // Load months on mount. URL is source of truth — only user actions update URL, NOT API responses.
  useEffect(() => {
    async function loadMonths() {
      const cached = readDaySnapshot('months', uid);
      if (cached) setMonths(cached);
      else setLoading(prev => ({ ...prev, months: true }));
      try {
        const data = await fetchMonths();
        setMonths(data.months);
        writeDaySnapshot('months', uid, data.months);
      } catch (err) {
        setError(`Failed to load months: ${err.message}`);
        if (process.env.NODE_ENV === 'development') console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, months: false }));
      }
    }
    loadMonths();
  }, [uid]);

  // Load stylists when store changes. URL is source of truth — only user actions update URL, NOT API responses.
  useEffect(() => {
    async function loadStylists() {
      if (!selectedStore) {
        setStylists([]);
        setUnregisteredStylists([]);
        return;
      }
      const cached = readDaySnapshot('stylists', selectedStore);
      if (cached) {
        setStylists(cached.stylists);
        setUnregisteredStylists(cached.unregistered);
      } else {
        setLoading(prev => ({ ...prev, stylists: true }));
      }
      try {
        const data = await fetchStylists(selectedStore);
        // "フリー" is the POS bucket for unassigned bookings, not a stylist.
        const names = (data.stylists || []).filter((name) => !PLACEHOLDER_STYLISTS.has(name));
        setStylists(names);
        setUnregisteredStylists(data.unregistered || []);
        writeDaySnapshot('stylists', selectedStore, { stylists: names, unregistered: data.unregistered || [] });
      } catch (err) {
        setError(`Failed to load stylists: ${err.message}`);
        if (process.env.NODE_ENV === 'development') console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, stylists: false }));
      }
    }
    loadStylists();
  }, [selectedStore]);

  // Load KPIs — paint session cache first (Settings refresh), then revalidate BQ
  const loadKPIs = useCallback(async () => {
    const cached = readCachedKpis(selectedStore, selectedStylist, selectedMonth);
    if (cached?.kpis) {
      setKpis(cached.kpis);
      loadedKpiKeyRef.current = cached.key;
    } else {
      setLoading((prev) => ({ ...prev, kpis: true }));
    }
    try {
      const data = await fetchKPIs({
        store: selectedStore,
        stylist: selectedStylist,
        month: selectedMonth,
      });
      setKpis(data.kpis);
      setDataAsOf(data.data_as_of ?? null);
      setKpiBreakdown(Array.isArray(data.breakdown) && data.breakdown.length > 1 ? data.breakdown : null);
      if (selectedStore && selectedStylist && selectedMonth) {
        loadedKpiKeyRef.current = makeFilterKey(selectedStore, selectedStylist, selectedMonth);
        writeCachedKpis(selectedStore, selectedStylist, selectedMonth, data.kpis);
      }
    } catch (err) {
      setError(`Failed to load KPIs: ${err.message}`);
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading((prev) => ({ ...prev, kpis: false }));
    }
  }, [selectedStore, selectedStylist, selectedMonth]);

  // Load store average KPIs when store/month changes
  const loadStoreAverageKPIs = useCallback(async () => {
    const avgKey = `${selectedStore || ''}|${selectedMonth || ''}`;
    const cached = readDaySnapshot('storeAvg', avgKey);
    if (cached !== null) setStoreAverageKpis(cached);
    else setLoading(prev => ({ ...prev, storeAverageKpis: true }));
    try {
      const data = await fetchStoreAverageKPIs({
        store: selectedStore,
        month: selectedMonth
      });
      setStoreAverageKpis(data.kpis);
      writeDaySnapshot('storeAvg', avgKey, data.kpis ?? null);
    } catch (err) {
      setError(`Failed to load store average KPIs: ${err.message}`);
      if (process.env.NODE_ENV === 'development') console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, storeAverageKpis: false }));
    }
  }, [selectedStore, selectedMonth]);

  // store|stylist whose history was last fetched fresh (not the session cache) — the month
  // fallback below must not act on a previous stylist's history
  const historyForRef = useRef('');

  // Month list is global (any stylist). A stylist whose data stops earlier (moved store, left)
  // would open on a month with no 実績 and show only 「—」. When the selected month is AFTER this
  // stylist's last month with data, show that last month instead. Earlier empty months stay as
  // chosen (a gap the user picked on purpose).
  useEffect(() => {
    if (!selectedMonth || historyForRef.current !== `${selectedStore}|${selectedStylist}`) return;
    const last = (kpiHistory || [])
      // > 0, not != null: the API coerces empty aggregates to 0
      .filter((r) => Number(r?.total_sales) > 0 && (r.month || r.ym))
      .map((r) => r.month || r.ym)
      .sort()
      .pop();
    if (last && selectedMonth > last && months.includes(last)) updateMonth(last, { replace: true });
  }, [kpiHistory, selectedMonth, selectedStore, selectedStylist, months, updateMonth]);

  // Load KPI history — session cache first, then revalidate
  const loadKPIHistory = useCallback(async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 [DEBUG] loadKPIHistory called with:', { selectedStore, selectedStylist });
    }
    const cached = readCachedKpiHistory(selectedStore, selectedStylist);
    if (cached?.history?.length) {
      setKpiHistory(cached.history);
    } else {
      setLoading((prev) => ({ ...prev, kpiHistory: true }));
    }
    try {
      const data = await fetchKPIHistory({
        store: selectedStore,
        stylist: selectedStylist,
      });
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 [DEBUG] KPI History response:', data);
      }
      const history = data.history || [];
      historyForRef.current = `${selectedStore}|${selectedStylist}`;
      setKpiHistory(history);
      writeCachedKpiHistory(selectedStore, selectedStylist, history);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('📊 [DEBUG] KPI History ERROR:', err);
      setError(`Failed to load KPI history: ${err.message}`);
    } finally {
      setLoading((prev) => ({ ...prev, kpiHistory: false }));
    }
  }, [selectedStore, selectedStylist]);

  // Load ML forecasts for graph prediction lines
  const loadMLForecasts = useCallback(async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔮 [DEBUG] loadMLForecasts called with:', { selectedStore, selectedStylist });
    }
    const fcKey = `${selectedStore || ''}|${selectedStylist || ''}`;
    const cached = readDaySnapshot('forecasts', fcKey);
    if (cached) setMlForecasts(cached);
    else setLoading(prev => ({ ...prev, mlForecasts: true }));
    try {
      const data = await fetchMLForecasts({
        store: selectedStore,
        stylist: selectedStylist
      });
      setMlForecasts(data.forecasts || []);
      writeDaySnapshot('forecasts', fcKey, data.forecasts || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('🔮 [DEBUG] ML Forecasts ERROR:', err);
      // Don't set error - forecasts are optional
      setMlForecasts([]);
    } finally {
      setLoading(prev => ({ ...prev, mlForecasts: false }));
    }
  }, [selectedStore, selectedStylist]);

  // Load AI comments for stylist performance analysis (All 4 sections)
  const loadAIComment = useCallback(async () => {
    if (!selectedStore || !selectedStylist || !selectedMonth) {
      setAiComment(null);
      setAiCommentSection1(null);
      setAiCommentSection2(null);
      setAiCommentSection3(null);
      return;
    }
    const aiKey = `${selectedStore}|${selectedStylist}|${selectedMonth}`;
    const cachedAi = readDaySnapshot('aiComment', aiKey);
    if (cachedAi) {
      setAiCommentSection1(cachedAi.s1 ?? null);
      setAiCommentSection2(cachedAi.s2 ?? null);
      setAiCommentSection3(cachedAi.s3 ?? null);
      setAiComment(cachedAi.s4 ?? null);
    } else {
      setLoading(prev => ({ 
        ...prev, 
        aiComment: true, 
        aiCommentSection1: true,
        aiCommentSection2: true,
        aiCommentSection3: true
      }));
    }
    // Only the open month gets "today"; for a closed month the server serves that
    // month's final card rather than one dated today.
    const cardDate = selectedMonth === today.slice(0, 7) ? today : null;
    const t0 = performance.now();
    console.log(`[AI-COMMENT] FETCH START  store=${selectedStore}  stylist=${selectedStylist}  month=${selectedMonth}  date=${cardDate || 'month-end'}`);
    try {
      const data = await fetchAIComment({
        store: selectedStore,
        stylist: selectedStylist,
        month: selectedMonth,
        date: cardDate
      });
      const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
      console.log(`[AI-COMMENT] FETCH DONE   ${elapsed}s  cached=${data.cached}(m=${data.month_cached} d=${data.day_cached})  date=${data.date}  agent=${data.agent_used || 'none'}  s1=${(data.section1_comment || '').length}ch  s2=${(data.section2_comment || '').length}ch  s3=${(data.section3_comment || '').length}ch  s4=${(data.ai_comment || data.section4_comment || '').length}ch`);
      setAiCommentSection1(data.section1_comment || null);
      setAiCommentSection2(data.section2_comment || null);
      setAiCommentSection3(data.section3_comment || null);
      setAiComment(data.ai_comment || data.section4_comment || null);
      writeDaySnapshot('aiComment', aiKey, {
        s1: data.section1_comment || null,
        s2: data.section2_comment || null,
        s3: data.section3_comment || null,
        s4: data.ai_comment || data.section4_comment || null,
      });
    } catch (err) {
      const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
      console.error(`[AI-COMMENT] FETCH ERROR  ${elapsed}s  ${err.message}`);
      setError(`Failed to load AI comment: ${err.message}`);
      setAiComment(null);
      setAiCommentSection1(null);
      setAiCommentSection2(null);
      setAiCommentSection3(null);
    } finally {
      setLoading(prev => ({ 
        ...prev, 
        aiComment: false, 
        aiCommentSection1: false,
        aiCommentSection2: false,
        aiCommentSection3: false
      }));
    }
  }, [selectedStore, selectedStylist, selectedMonth, today]);

  // One BQ snapshot per store|stylist|month — Home / AI / Settings share context.kpis
  // (same idea as AI chat kpi_summary: no second round-trip when filters unchanged).
  const prevFiltersRef = useRef({ store: null, stylist: null, month: null, day: null });
  const loadedDayRef = useRef('');
  
  useEffect(() => {
    // Wait until the open-on-main-store step has run, or a URL store would load first
    // and then be replaced by home_store (two BQ loads, wrong screen in between).
    if (user && !hasRestoredRef.current) return;
    const prev = prevFiltersRef.current;
    const filtersChanged = prev.store !== selectedStore || 
                           prev.stylist !== selectedStylist || 
                           prev.month !== selectedMonth ||
                           prev.day !== today;
    
    if (!filtersChanged) {
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 [DEBUG] Filter changed:', { selectedStore, selectedStylist, selectedMonth });
    }
    prevFiltersRef.current = { store: selectedStore, stylist: selectedStylist, month: selectedMonth, day: today };
    
    if (!(selectedStore && selectedStylist && selectedMonth)) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '⚠️ [DEBUG] KPI load skipped — need store+stylist+month:',
          { store: !!selectedStore, stylist: !!selectedStylist, month: !!selectedMonth }
        );
      }
      return;
    }

    const key = makeFilterKey(selectedStore, selectedStylist, selectedMonth);
    // Keep existing snapshot when navigating Home → Settings with same filters.
    // A new JST day is not "same filters" — the ETL has landed since.
    if (key && key === loadedKpiKeyRef.current && loadedDayRef.current === today && kpis) {
      if (process.env.NODE_ENV === 'development') {
        console.log('♻️ [DEBUG] Reusing home KPI snapshot — skip BQ', key);
      }
      return;
    }

    loadedDayRef.current = today;
    loadKPIs();
    loadStoreAverageKPIs();
    loadKPIHistory();
    loadMLForecasts();
    loadAIComment();
    // myProfile: re-run in the same commit the open step runs (it is declared above this effect)
  }, [user, myProfile, selectedStore, selectedStylist, selectedMonth, today, kpis, loadKPIs, loadStoreAverageKPIs, loadKPIHistory, loadMLForecasts, loadAIComment]);

  // Format month for display (2025-10 → Oct 2025)
  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
    return `${year}年${monthNames[parseInt(month) - 1]}`;
  };

  // Parse display month back to API format (2025年10月 → 2025-10)
  const parseMonthDisplay = (displayStr) => {
    if (!displayStr) return '';
    const match = displayStr.match(/(\d{4})年(\d{1,2})月/);
    if (match) {
      return `${match[1]}-${match[2].padStart(2, '0')}`;
    }
    return displayStr;
  };

  const value = {
    // Filters (URL is source of truth)
    selectedStore,
    setSelectedStore: updateStore,
    selectedStylist,
    setSelectedStylist: updateStylist,
    selectedMonth,
    setSelectedMonth: updateMonth,

    // Own identity (/auth/me) — see myProfile above
    myProfile,
    setMyProfile,

    // Data
    stores,
    stylists,
    unregisteredStylists,
    months,
    kpis,
    dataAsOf,
    kpiBreakdown,
    storeAverageKpis,
    kpiHistory,
    mlForecasts,
    aiComment,
    today,
    aiCommentSection1,
    aiCommentSection2,
    aiCommentSection3,

    // Loading
    loading,
    isLoading: Object.values(loading).some(Boolean),

    // Error
    error,
    clearError: () => setError(null),

    // Helpers
    formatMonthDisplay,
    parseMonthDisplay,
    
    // Manual refresh
    refreshKPIs: loadKPIs,
    refreshStoreAverageKPIs: loadStoreAverageKPIs,
    refreshKPIHistory: loadKPIHistory,
    refreshMLForecasts: loadMLForecasts,
    refreshAIComment: loadAIComment
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

export default DashboardContext;

